import React, { useMemo } from 'react';
import { useHomepageProps } from '../../hooks/useHomepageProps';
import { ContentfulEntry } from '@jbhi-fi/apis/src/contentful/types';
import { ContentEntry } from '../../ContentEntry/ContentEntry';
import { getContentEntryBlocks } from './entryContentBlockPropsMap';
import { ContentBlock } from '../../ContentBlock';
import { styles } from './Homepage.css';
import { ContentBlockProps } from '../../ContentBlock/ContentBlock';
import { useContentfulAds } from '../../hooks/useContentfulAds';
import { usePublicApiClient } from '../../hooks/usePublicApiClient';
import { HeroBannerCarousel, HeroBannerCarouselProps } from '../../HeroBannerCarousel/HeroBannerCarousel';
import {
  CarouselBannerTransformer,
  isPercentOffBanner,
  isProductBanner,
  isSupplierBanner,
} from '../../Contentful/transformers/CarouselBannerTransformer';
import { TrackingProvider } from '../../Tracking';
import { Disclaimer } from '../../Disclaimer';
import { CarouselTransformer } from '../../Contentful/transformers/CarouselTransformer';
import { TabsBlock } from '../../TabsBlock/TabsBlock';
import { ProductBlockTransformer } from '../../Contentful/transformers/ProductBlockTransformer';
import { ErrorBoundary } from '../../ErrorBoundary/ErrorBoundary';
import { TakeoverBlock } from '../../TakeoverBlock';
import { TakeoverBlockProps } from '../../TakeoverBlock/TakeoverBlock';
import { PRODUCT_TRACKING_DATA } from '../../context/HomepagePropsProvider';
import { PromoCard } from '../../PromoCard/PromoCard';
import { PromoCardTransformer } from '../../Contentful/transformers/PromoCardTransformer';
import { useCustomerContext } from '../../context/CustomerContextProvider';
import { ContactBlockTransformer } from '../../Contentful/transformers/ContactBlockTransformer';
import { ListingBlockTransformer } from '../../Contentful/transformers/ListingBlockTransformer';
import { ListingBlock } from '../../ListingBlock';
import { ArticleBlockTransformer } from '../../Contentful/transformers/ArticleBlockTransformer';
import { LeaderboardBannerTransformer } from '../../Contentful/transformers/LeaderboardBannerTransformer';
import { HeroBanner } from '../../HeroBanner/HeroBanner';

const MAX_NUM_HERO_BANNERS = 8;

export type HomepageProps = {
  takeoverBlock?: TakeoverBlockProps;
  heroBannerCarouselProps?: Omit<HeroBannerCarouselProps, 'heroBanners' | 'isLoading'>;
  sections: Array<ContentfulEntry<any>>;
  disclaimers?: Array<string>;
  salesLineHours?: string;
  contactBlockComponent?: React.VFC<any>;
};

export const Homepage = () => {
  const { data, isLoading, error } = useHomepageProps();
  const { contentfulThemeApiClient } = usePublicApiClient();

  const {
    banners,
    error: bannerError,
    isLoading: isBannersLoading,
  } = useContentfulAds({ queryOptions: { handle: 'homepage' }, contentfulThemeApiClient });
  const filteredBanners = banners
    .filter(({ entry }) => {
      if (!isPercentOffBanner(entry) && !isProductBanner(entry) && !isSupplierBanner(entry)) {
        return entry?.fields?.dynamicImageUrls
          ? entry?.fields?.desktopHeroBannerUrl && entry?.fields?.mobileHeroBannerUrl
          : entry?.fields?.desktopHeroBanner && entry?.fields?.mobileHeroBanner;
      }
      return entry;
    })
    .slice(0, MAX_NUM_HERO_BANNERS);

  if (error) {
    console.log(error);
    return null;
  }
  if (!data && isLoading) return <div>Loading...</div>;
  if (!data) return null;

  const { takeoverBlock, heroBannerCarouselProps, sections = [], disclaimers } = data;

  const getHeroContent = () => {
    if (!isLoading && takeoverBlock) {
      return <TakeoverBlock {...takeoverBlock} />;
    }
    if (isBannersLoading || (filteredBanners?.length && !bannerError)) {
      return (
        <HeroBannerCarousel
          {...(heroBannerCarouselProps ?? {})}
          {...CarouselBannerTransformer.toHeroBannerCarouselProps(filteredBanners)}
          isLoading={isBannersLoading}
          variant="homepage"
        />
      );
    }
    return null;
  };

  return (
    <TrackingProvider category="Homepage" context="homepage">
      <div className={styles.container}>
        {getHeroContent()}
        {sections.map((section, i) => {
          return (
            <ErrorBoundary key={i} errorMessageProps={{ variant: 'contentBlock' }}>
              <HomepageSection entry={section} />
            </ErrorBoundary>
          );
        })}
        {disclaimers ? (
          <ContentBlock {...DEFAULT_CONTENT_BLOCK_PROPS} backgroundColor="secondary.main">
            <Disclaimer disclaimers={disclaimers} />
          </ContentBlock>
        ) : null}
      </div>
    </TrackingProvider>
  );
};

const DEFAULT_CONTENT_BLOCK_PROPS: Partial<ContentBlockProps> = {
  paddingX: { mobile: 4, tablet: 8 },
  paddingY: { mobile: 7.5, tablet: 15 },
};

const HomepageSection = ({ entry }: { entry: ContentfulEntry<any> }) => {
  const { isLoggedIn } = useCustomerContext();
  const { data } = useHomepageProps();
  const contentBlockProps = useMemo(() => {
    const entryContentBlockProps = getContentEntryBlocks({ isLoggedIn })[entry?.sys?.contentType?.sys?.id ?? ''];
    if (typeof entryContentBlockProps === 'function') {
      return entryContentBlockProps(entry);
    }
    return entryContentBlockProps;
  }, [entry]);

  if (!contentBlockProps) return null;

  return (
    <ContentBlock
      maxWidth={{ mobile: 'xlarge', xxxl: 'ultralarge' }}
      {...DEFAULT_CONTENT_BLOCK_PROPS}
      {...(contentBlockProps ?? {})}
    >
      <ContentEntry
        entry={entry}
        overrides={{
          leaderboardBanner: {
            Component: HeroBanner,
            transformer: e =>
              LeaderboardBannerTransformer.toHeroBannerProps(e, 'homepage-leaderboard-banner', 'homepage', true),
          },
          articleBlock: {
            Component: TabsBlock,
            transformer: e =>
              ArticleBlockTransformer.toTabsBlockProps(e, {
                testId: 'homepage-article-block',
              }),
          },
          listingBlock: {
            Component: ListingBlock,
            transformer: e => ListingBlockTransformer.toListingBlockProps(e, { testId: 'homepage-listing-block' }),
          },
          carousel: e =>
            CarouselTransformer.toContentEntryProps(e, {
              productTrackingData: PRODUCT_TRACKING_DATA,
              isLoggedIn,
              testId: 'homepage-carousel-block',
            }),
          productBlock: {
            Component: TabsBlock,
            transformer: e =>
              ProductBlockTransformer.toTabsBlockProps(e, {
                productTrackingData: PRODUCT_TRACKING_DATA,
                testId: 'homepage-product-block',
              }),
          },
          promoCard: {
            Component: PromoCard,
            transformer: e => PromoCardTransformer.toPromoCardProps(e, { isLoggedIn }),
          },
          contactBlock: {
            Component: data?.contactBlockComponent ?? (() => null),
            transformer: e =>
              ContactBlockTransformer.toContactBlockProps(e, {
                salesLineHours: data?.salesLineHours,
                testId: 'homepage-contact-block',
              }),
          },
        }}
      />
    </ContentBlock>
  );
};
